import { getRoot, types } from 'mobx-state-tree';
import { createThunk, model, Model } from 'mst-collection';
import { format, parse } from 'date-fns';

import { Api } from '@api';
import type { RootModelType } from '../RootModel';
import { ThunkAbortController } from '../utils';
import { capitalize } from '@utils';
import { enumerationNumber } from '../types';
import {
  PaymentMethodTypeEnum,
  PaymentMethodWalletEnum,
} from '@constants';

export class PaymentMethod extends Model({
  id: types.identifier,
  type: enumerationNumber([
    PaymentMethodTypeEnum.Card,
    PaymentMethodTypeEnum.PayPal,
    PaymentMethodTypeEnum.Link,
  ]),
  last4: types.maybeNull(types.string),
  expMonth: types.maybeNull(types.string),
  expYear: types.maybeNull(types.string),
  brand: types.maybeNull(types.string),
  email: types.maybeNull(types.string),
  payerId: types.maybeNull(types.string),
  country: types.maybeNull(types.string),
  wallet: types.maybeNull(
    enumerationNumber([
      PaymentMethodWalletEnum.GooglePay,
      PaymentMethodWalletEnum.ApplePay,
    ]),
  ),
  isDefault: false,
}) {
  get exp() {
    if (!this.expMonth || !this.expYear) {
      return '';
    }

    return `${this.expMonth}/${format(
      parse(this.expYear, 'yyyy', new Date()),
      'yy',
    )}`;
  }

  get formattedNumber() {
    return `**** **** **** ${this.last4}`;
  }

  get formattedBrand() {
    if (!this.brand) {
      return '';
    }

    return capitalize(this.brand);
  }

  setIsDefault(value: boolean) {
    this.isDefault = value;
  }

  delete = createThunk(() => {
    return async function deletePaymentMethod(this: PaymentMethod) {
      await Api.Payments.deletePaymentMethod({
        id: this.id,
      });

      getRoot<RootModelType>(
        this,
      ).viewer.paymentMethodList.removeById(this.id);
    };
  });

  private _setAsDefaultAbortController = new ThunkAbortController();

  setAsDefault = createThunk(() => {
    return async function setAsDefault(this: PaymentMethod) {
      const previousDefaultPaymentMethod = getRoot<RootModelType>(
        this,
      ).viewer.paymentMethodList.array.find((item) => item.isDefault);

      previousDefaultPaymentMethod?.setIsDefault(false);

      try {
        // Cancel previous request if it in progress
        this._setAsDefaultAbortController.abort();

        this.setIsDefault(true);

        await Api.Payments.updatePaymentMethod(
          { id: this.id },
          this._setAsDefaultAbortController.create(),
        );

        this._setAsDefaultAbortController.remove();
      } catch (error) {
        // If request was canceled do nothing
        if (ThunkAbortController.isCanceled(error)) {
          return;
        }

        // Change all values back
        this.setIsDefault(false);
        previousDefaultPaymentMethod?.setIsDefault(true);

        throw error;
      }
    };
  });
}

export const PaymentMethodModel = model(PaymentMethod);
